/**
 * Macro #26 · Increment 2 — PUBLIC 3D overlay (NO three; hosts the lazy scene).
 *
 * A focused "your territory as a voxel world" surface opened from the Level-2
 * ENTER 3D affordance. The 2D Grid + published Canvas stay perceptible beneath
 * the translucent backdrop (Canvas underneath attenuated). A genuine WebGL
 * fallback keeps the Canvas readable — never a black rectangle or infinite
 * loader.
 */
import { useEffect, useState, type CSSProperties } from "react";

import { LazyVoxelScene } from "../voxel3d/LazyVoxelScene";
import type { WorkArt } from "../domain/voxel3d";
import type { PublicSnapshot } from "../lib/publication-api";
import type { FloorRect } from "../voxel3d/canvasArtFloor";

export interface Public3DTarget {
  identityId: string;
  displayName: string | null;
  workArts: WorkArt[];
  voxelCount: number;
  /** Territory's on-screen rectangle (px, grid-wrap space) at Enter time, used
   * to anchor the grid-integrated scene directly over the parcel. */
  anchorRect?: { x: number; y: number; w: number; h: number };
  /** Published snapshot + world footprint used to texture the 3D floor with the
   * territory's real 2D Canvas art (grid-integrated mode only). */
  artSnapshot?: PublicSnapshot | null;
  artFootprint?: FloorRect | null;
}

function Loading() {
  return (
    <div
      data-testid="public-3d-loading"
      style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", color: "#7fe9f0", font: "600 12px/1.4 'Inter',system-ui,sans-serif", letterSpacing: "0.16em", textTransform: "uppercase" }}
    >
      Loading voxel world…
    </div>
  );
}

function Fallback({ onExit }: { onExit: () => void }) {
  return (
    <div
      data-testid="public-3d-fallback"
      style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", padding: 24 }}
    >
      <div style={{ maxWidth: 380, textAlign: "center", color: "#c6d2dc", font: "400 13px/1.6 'Inter',system-ui,sans-serif" }}>
        <div style={{ color: "#7fe9f0", letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>
          3D unavailable
        </div>
        This device could not start the 3D view. The published 2D Canvas remains
        available on the Grid.
        <div style={{ marginTop: 16 }}>
          <button data-testid="public-3d-fallback-exit" onClick={onExit} style={pillStyle}>
            Back to Canvas
          </button>
        </div>
      </div>
    </div>
  );
}

const pillStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  height: 32,
  padding: "0 14px",
  borderRadius: 999,
  border: "1px solid rgba(34,211,238,0.5)",
  background: "rgba(6,11,18,0.7)",
  color: "#c9f7ff",
  font: "600 11px/1 'Inter',system-ui,sans-serif",
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  cursor: "pointer",
};

export function Public3DOverlay({ target, onExit, variant = "standalone" }: { target: Public3DTarget; onExit: () => void; variant?: "grid" | "standalone" }) {
  const [resetSignal, setResetSignal] = useState(0);
  const [fitSignal, setFitSignal] = useState(0);
  const isGrid = variant === "grid";
  // Grid-integrated entrance: the scene tilts UP into place over the real Grid,
  // so it reads as "I tilted my territory into 3D" — not "I opened a viewer".
  const [entered, setEntered] = useState(!isGrid);
  useEffect(() => {
    if (!isGrid) return;
    const r = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(r);
  }, [isGrid]);
  // One-shot guidance hint, auto-fades so it never obstructs the demo.
  const [hintVisible, setHintVisible] = useState(isGrid);
  useEffect(() => {
    if (!isGrid) return;
    const t = window.setTimeout(() => setHintVisible(false), 5200);
    return () => window.clearTimeout(t);
  }, [isGrid]);

  // Grid-integrated scene fills the WHOLE overlay (fullscreen) so a zoomed-out
  // parcel is never clipped by a small anchored square; keep the tilt-into-place
  // entrance. Standalone fills the area below its header bar.
  let sceneStyle: CSSProperties = isGrid
    ? { position: "absolute", inset: 0, zIndex: 1 }
    : { position: "relative", flex: 1, minHeight: 0, zIndex: 1 };
  if (isGrid) {
    sceneStyle = {
      ...sceneStyle,
      transformOrigin: "50% 92%",
      transition: "transform 640ms cubic-bezier(0.22,0.61,0.36,1), opacity 520ms ease-out",
      transform: entered
        ? "perspective(1500px) rotateX(0deg) translateY(0) scale(1)"
        : "perspective(1500px) rotateX(13deg) translateY(5%) scale(0.95)",
      opacity: entered ? 1 : 0,
    };
  }

  return (
    <div
      data-testid="public-3d-overlay"
      data-variant={variant}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 40,
        // Grid-integrated: NEAR-OPAQUE so the flat 2D Grid is hidden (no double
        // image) — the "same territory" recognition comes from the real Canvas
        // art textured onto the 3D floor. Standalone keeps its premium backdrop.
        background: isGrid
          ? "radial-gradient(ellipse at 50% 46%, rgba(6,10,17,0.90) 0%, rgba(3,6,11,0.97) 62%, #02040a 100%)"
          : "rgba(4,7,13,0.86)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Standalone keeps the full-width top header. In grid-integrated mode
          the header is REPLACED by a left-side vertical menu (below) so the
          territory identity never sits under the Grid's Instrument bar. */}
      {!isGrid && (
        <div
          style={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "10px 14px",
            borderBottom: "1px solid rgba(34,211,238,0.14)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <span style={{ color: "#7fe9f0", font: "700 11px/1 'Inter',system-ui,sans-serif", letterSpacing: "0.2em", textTransform: "uppercase" }}>
              3D · Voxel World
            </span>
            <span
              data-testid="public-3d-name"
              style={{ color: "#c6d2dc", font: "600 12px/1 'Inter',system-ui,sans-serif", letterSpacing: "0.06em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {target.displayName || "Untitled Territory"}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button data-testid="reset-3d-view-btn" onClick={() => setResetSignal((s) => s + 1)} style={pillStyle}>
              Reset View
            </button>
            <button
              data-testid="exit-3d-btn"
              onClick={onExit}
              style={{ ...pillStyle, borderColor: "rgba(255,255,255,0.25)", color: "#e6edf3" }}
            >
              Exit 3D
            </button>
          </div>
        </div>
      )}
      {isGrid && (
        <div
          data-testid="grid-3d-side-menu"
          style={{
            position: "absolute",
            left: 16,
            top: 76,
            zIndex: 4,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            padding: "14px 14px",
            width: 184,
            borderRadius: 16,
            border: "1px solid rgba(34,211,238,0.22)",
            background: "rgba(6,11,18,0.74)",
            backdropFilter: "blur(12px)",
            boxShadow: "0 10px 34px rgba(0,0,0,0.5)",
          }}
        >
          <span style={{ color: "#7fe9f0", font: "700 10px/1.2 'Inter',system-ui,sans-serif", letterSpacing: "0.22em", textTransform: "uppercase" }}>
            Grid · Tilted 3D
          </span>
          <span
            data-testid="public-3d-name"
            style={{ color: "#e6edf3", font: "600 14px/1.3 'Inter',system-ui,sans-serif", letterSpacing: "0.03em", wordBreak: "break-word" }}
          >
            {target.displayName || "Untitled Territory"}
          </span>
          <div aria-hidden style={{ height: 1, background: "rgba(34,211,238,0.16)", margin: "2px 0" }} />
          <button
            data-testid="fit-parcel-3d-btn"
            onClick={() => setFitSignal((s) => s + 1)}
            style={{ ...pillStyle, width: "100%", justifyContent: "center", borderColor: "rgba(34,211,238,0.6)", background: "rgba(12,26,36,0.8)" }}
          >
            Fit Parcel
          </button>
          <button
            data-testid="reset-3d-view-btn"
            onClick={() => setResetSignal((s) => s + 1)}
            style={{ ...pillStyle, width: "100%", justifyContent: "center" }}
          >
            Reset View
          </button>
          <button
            data-testid="exit-3d-btn"
            onClick={onExit}
            style={{ ...pillStyle, width: "100%", justifyContent: "center", borderColor: "rgba(255,255,255,0.25)", color: "#e6edf3" }}
          >
            Exit 3D
          </button>
        </div>
      )}
      <div style={sceneStyle}>
        <LazyVoxelScene
          workArts={target.workArts}
          sceneOpacity={1}
          transparent={isGrid}
          artSnapshot={isGrid ? target.artSnapshot ?? null : null}
          artFootprint={isGrid ? target.artFootprint ?? null : null}
          resetSignal={resetSignal}
          fitSignal={fitSignal}
          loading={<Loading />}
          fallback={<Fallback onExit={onExit} />}
        />
      </div>
      {isGrid && (
        <div
          data-testid="grid-3d-hint"
          style={{
            position: "absolute",
            left: "50%",
            bottom: 20,
            transform: "translateX(-50%)",
            zIndex: 3,
            pointerEvents: "none",
            opacity: hintVisible ? 1 : 0,
            transition: "opacity 500ms ease",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "7px 14px",
            borderRadius: 999,
            border: "1px solid rgba(34,211,238,0.35)",
            background: "rgba(6,11,18,0.72)",
            backdropFilter: "blur(8px)",
            color: "#c9f7ff",
            font: "600 11px/1 'Inter',system-ui,sans-serif",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            boxShadow: "0 4px 18px rgba(0,0,0,0.45)",
          }}
        >
          Trascina: ruota · Tasto destro: sposta · Rotella: zoom
        </div>
      )}
    </div>
  );
}
